import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import AdminHeader from '../components/AdminHeader';
import './AdminReports.css';

const AdminReports = () => {
  const [reportType, setReportType] = useState('sales');
  const [period, setPeriod] = useState('month');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [stockStatus, setStockStatus] = useState('all');
  const [category] = useState('all');
  const [limit, setLimit] = useState(20);

  const API_BASE = (process.env.REACT_APP_API || 'http://localhost:5000').replace(/\/+$/, '') + '/api';

  const reportTypes = [
    { value: 'sales', label: 'Sales Report', icon: '💰' },
    { value: 'inventory-list', label: 'Inventory List', icon: '📦' },
    { value: 'product-master-list', label: 'Product Master List', icon: '📋' },
    { value: 'fast-moving', label: 'Fast Moving Products', icon: '🚀' },
    { value: 'slow-moving', label: 'Slow Moving Products', icon: '🐌' },
    { value: 'inventory-changes', label: 'Inventory Changes', icon: '📊' }
  ];

  const periods = [
    { value: 'today', label: 'Today' },
    { value: 'yesterday', label: 'Yesterday' },
    { value: 'week', label: 'This Week' },
    { value: 'month', label: 'This Month' },
    { value: 'quarter', label: 'This Quarter' },
    { value: 'year', label: 'This Year' },
    { value: 'custom', label: 'Custom Range' }
  ];

  const stockStatuses = [
    { value: 'all', label: 'All Stock' },
    { value: 'in-stock', label: 'In Stock (>10)' },
    { value: 'low-stock', label: 'Low Stock (1-10)' },
    { value: 'out-of-stock', label: 'Out of Stock (0)' }
  ];

  const generateReport = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('token');
      let url = `${API_BASE}/reports/${reportType}`;
      const params = new URLSearchParams();

      if (['sales', 'fast-moving', 'slow-moving', 'inventory-changes'].includes(reportType)) {
        params.append('period', period);
        if (period === 'custom') {
          if (customStart) params.append('customStart', customStart);
          if (customEnd) params.append('customEnd', customEnd);
        }
      }

      if (['fast-moving', 'slow-moving'].includes(reportType)) {
        params.append('limit', limit);
      }

      if (reportType === 'inventory-list') {
        if (stockStatus !== 'all') params.append('stockStatus', stockStatus);
        if (category !== 'all') params.append('category', category);
      }

      if (params.toString()) url += `?${params.toString()}`;

      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setReportData(response.data);
    } catch (err) {
      console.error('Report generation error:', err);
      setError(err.response?.data?.message || err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    const n = parseFloat(amount);
    if (Number.isNaN(n)) return '₱0.00';
    return `₱${n.toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  useEffect(() => {
    setReportData(null);
    setError(null);
  }, [reportType]);

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-PH', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatDateTime = (date) => {
    return new Date(date).toLocaleString('en-PH', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const exportToPDF = () => window.print();

  const exportToCSV = () => {
    if (!reportData) return;

    let csvContent = '';
    const summary = reportData.summary;

    csvContent += `${summary.reportType}\n`;
    csvContent += `Generated: ${formatDateTime(summary.generatedAt)}\n`;
    csvContent += `Period: ${formatDate(summary.startDate || new Date())} to ${formatDate(summary.endDate || new Date())}\n\n`;

    if (reportType === 'sales' && reportData.orders) {
      csvContent += 'Order ID,Date,Customer,Items,Total Amount,Status,Payment Method\n';
      reportData.orders.forEach(order => {
        const orderDate = formatDateTime(order.createdAt);
        const customer = (order.deliveryDetails && (order.deliveryDetails.firstName || order.deliveryDetails.lastName))
          ? `${order.deliveryDetails.firstName || ''} ${order.deliveryDetails.lastName || ''}`.trim()
          : (order.user ? `${order.user.firstName || ''} ${order.user.lastName || ''}`.trim() : 'N/A');
        const items = order.items.length;
        const total = order.totalAmount;
        const status = order.status;
        const payment = order.paymentMethod || 'cash';
        csvContent += `${order._id},${orderDate},${customer},${items},${total},${status},${payment}\n`;
      });
    } else if (reportType === 'inventory-list' && reportData.products) {
      csvContent += 'Product Name,Category,Stock,Price,Status\n';
      reportData.products.forEach(p => {
        const status = p.stock > 10 ? 'In Stock' : p.stock > 0 ? 'Low Stock' : 'Out of Stock';
        csvContent += `${p.name},${p.category},${p.stock},${p.price},${status}\n`;
      });
    } else if (reportType === 'fast-moving' && reportData.products) {
      csvContent += 'Product Name,Category,Quantity Sold,Revenue,Orders\n';
      reportData.products.forEach(item => {
        csvContent += `${item.product.name},${item.product.category},${item.totalQuantity},${item.totalRevenue},${item.orderCount}\n`;
      });
    } else if (reportType === 'slow-moving' && reportData.products) {
      csvContent += 'Product Name,Category,Current Stock,Quantity Sold,Sales Velocity\n';
      reportData.products.forEach(item => {
        csvContent += `${item.product.name},${item.product.category},${item.stock},${item.totalQuantity},${item.salesVelocity.toFixed(4)}\n`;
      });
    }

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${reportType}-${Date.now()}.csv`;
    link.click();
  };

  if (loading) {
    return (
      <div style={{ background: '#f8f8f8', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ width: '100%', background: '#175c36', minHeight: 32, height: 32, position: 'sticky', top: 0, zIndex: 1001, boxShadow: '0 2px 8px rgba(23, 92, 54, 0.2)' }} />
        <div style={{ width: '100%', background: 'rgba(255, 255, 255, 0.95)', backdropFilter: 'blur(10px)', boxShadow: '0 2px 16px rgba(0,0,0,0.06)', position: 'sticky', top: 32, zIndex: 1000 }}>
          <div style={{ width: '100%', margin: '0 auto' }}>
            <AdminHeader />
          </div>
        </div>
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 20px' }}>
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1, rotate: 360 }}
            transition={{ duration: 0.6, rotate: { duration: 1, repeat: Infinity, ease: 'linear' } }}
            style={{ fontSize: '4rem' }}
          >
            🌱
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: '#f8f8f8', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Green Header Bar */}
      <div className="no-print" style={{ width: '100%', background: '#175c36', minHeight: 32, height: 32, position: 'sticky', top: 0, zIndex: 1001, boxShadow: '0 2px 8px rgba(23, 92, 54, 0.2)' }} />
      
      {/* Navbar */}
      <div className="no-print" style={{ width: '100%', background: 'rgba(255, 255, 255, 0.95)', backdropFilter: 'blur(10px)', boxShadow: '0 2px 16px rgba(0,0,0,0.06)', position: 'sticky', top: 32, zIndex: 1000 }}>
        <div style={{ width: '100%', margin: '0 auto' }}>
          <AdminHeader />
        </div>
      </div>

    <div className="admin-reports">
      {/* Page Header */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="reports-header"
      >
        <h1>📊 Business Reports</h1>
        <p>Generate comprehensive reports for business insights</p>
      </motion.div>

      {/* Report Controls */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.1 }}
        className="report-controls no-print"
      >
        <div className="control-group">
          <label>Report Type</label>
          <select value={reportType} onChange={(e) => setReportType(e.target.value)} aria-label="Select report type">
            {reportTypes.map(type => (
              <option key={type.value} value={type.value}>{type.icon} {type.label}</option>
            ))}
          </select>
        </div>

        {['sales', 'fast-moving', 'slow-moving', 'inventory-changes'].includes(reportType) && (
          <div className="control-group">
            <label>Period</label>
            <select value={period} onChange={(e) => setPeriod(e.target.value)} aria-label="Select period">
              {periods.map(p => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </select>
          </div>
        )}

        {period === 'custom' && ['sales', 'fast-moving', 'slow-moving', 'inventory-changes'].includes(reportType) && (
          <>
            <div className="control-group">
              <label>Start Date</label>
              <input
                type="date"
                value={customStart}
                onChange={(e) => setCustomStart(e.target.value)}
                aria-label="Start date"
              />
            </div>
            <div className="control-group">
              <label>End Date</label>
              <input
                type="date"
                value={customEnd}
                onChange={(e) => setCustomEnd(e.target.value)}
                aria-label="End date"
              />
            </div>
          </>
        )}

        {reportType === 'inventory-list' && (
          <div className="control-group">
            <label>Stock Status</label>
            <select value={stockStatus} onChange={(e) => setStockStatus(e.target.value)} aria-label="Stock status">
              {stockStatuses.map(s => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </div>
        )}

        {['fast-moving', 'slow-moving'].includes(reportType) && (
          <div className="control-group">
            <label>Top Items</label>
            <input
              type="number"
              min="5"
              max="100"
              value={limit}
              onChange={(e) => setLimit(e.target.value)}
              aria-label="Number of top items"
            />
          </div>
        )}

        <motion.button 
          whileHover={{ scale: loading ? 1 : 1.02 }}
          whileTap={{ scale: loading ? 1 : 0.98 }}
          className="btn-generate" 
          onClick={generateReport} 
          disabled={loading}
          aria-label="Generate report"
        >
          {loading ? '⏳ Generating...' : '✨ Generate Report'}
        </motion.button>
      </motion.div>

      {/* Error Message */}
      <AnimatePresence>
        {error && (
          <motion.div 
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="error-message no-print"
          >
            <strong>⚠️ Error:</strong> {error}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Report Display */}
      <AnimatePresence mode="wait">
        {reportData && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.4 }}
          >
            {/* Export Actions */}
            <div className="report-actions no-print">
              <motion.button 
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={exportToPDF} 
                className="btn-export"
                aria-label="Print or save as PDF"
              >
                <span>🖨️</span> Print / PDF
              </motion.button>
              <motion.button 
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={exportToCSV} 
                className="btn-export"
                aria-label="Export to CSV"
              >
                <span>📊</span> Export CSV
              </motion.button>
            </div>

            <div className="report-container">
              {/* Report Header */}
              <div className="report-header">
                <div className="company-info">
                  <h2>🌿 Chiqui's Garden</h2>
                  <p>Landscaping & Garden Services</p>
                  <p>📧 chiquisgardenlandscapingservic@gmail.com</p>
                  <p>📱 +63 936 898 3053</p>
                </div>
                <div className="report-meta">
                  <h3>{reportData.summary.reportType}</h3>
                  <p><strong>Generated:</strong> {formatDateTime(reportData.summary.generatedAt)}</p>
                  {reportData.summary.startDate && (
                    <p><strong>Period:</strong> {formatDate(reportData.summary.startDate)} to {formatDate(reportData.summary.endDate)}</p>
                  )}
                </div>
              </div>

            {/* SALES REPORT */}
            {reportType === 'sales' && (
              <>
                <div className="report-summary">
                  <div className="summary-card">
                    <h4>Total Orders</h4>
                    <p className="summary-value">{reportData.summary.totalOrders}</p>
                  </div>
                  <div className="summary-card">
                    <h4>Total Revenue</h4>
                    <p className="summary-value">{formatCurrency(reportData.summary.totalRevenue)}</p>
                  </div>
                  <div className="summary-card">
                    <h4>Gross Revenue</h4>
                    <p className="summary-value">{formatCurrency(reportData.summary.grossRevenue)}</p>
                  </div>
                  <div className="summary-card">
                    <h4>Items Sold</h4>
                    <p className="summary-value">{reportData.summary.totalItemsSold}</p>
                  </div>
                  <div className="summary-card">
                    <h4>Avg Order Value</h4>
                    <p className="summary-value">{formatCurrency(reportData.summary.averageOrderValue)}</p>
                  </div>
                  <div className="summary-card">
                    <h4>Avg Order Value (Gross)</h4>
                    <p className="summary-value">{formatCurrency(reportData.summary.averageOrderValueGross)}</p>
                  </div>
                </div>

                <div className="report-section">
                  <h4>Sales Breakdown by Category</h4>
                  <table className="report-table">
                    <thead>
                      <tr>
                        <th>Category</th>
                        <th>Quantity Sold</th>
                        <th>Revenue</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(reportData?.summary?.categoryBreakdown || {}).map(([cat, data]) => (
                        <tr key={cat}>
                          <td>{cat}</td>
                          <td>{data.quantity}</td>
                          <td>{formatCurrency(data.revenue)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {/* Mobile Cards */}
                  <div className="mobile-data-cards">
                    {Object.entries(reportData?.summary?.categoryBreakdown || {}).map(([cat, data]) => (
                      <div key={cat} className="mobile-card">
                        <div className="mobile-card-row">
                          <span className="mobile-card-label">Category</span>
                          <span className="mobile-card-value">{cat}</span>
                        </div>
                        <div className="mobile-card-row">
                          <span className="mobile-card-label">Quantity Sold</span>
                          <span className="mobile-card-value">{data.quantity}</span>
                        </div>
                        <div className="mobile-card-row">
                          <span className="mobile-card-label">Revenue</span>
                          <span className="mobile-card-value">{formatCurrency(data.revenue)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="report-section">
                  <h4>Order Details</h4>
                  <table className="report-table">
                    <thead>
                      <tr>
                        <th>Order ID</th>
                        <th>Date</th>
                        <th>Customer</th>
                        <th>Items</th>
                        <th>Total</th>
                        <th>Status</th>
                        <th>Payment</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(reportData?.orders || []).map(order => (
                        <tr key={order._id}>
                          <td>{order._id.slice(-8).toUpperCase()}</td>
                          <td>{formatDate(order.createdAt)}</td>
                          <td>{(order.deliveryDetails && (order.deliveryDetails.firstName || order.deliveryDetails.lastName))
                            ? `${order.deliveryDetails.firstName || ''} ${order.deliveryDetails.lastName || ''}`.trim()
                            : (order.user ? `${order.user.firstName || ''} ${order.user.lastName || ''}`.trim() : 'N/A')}
                          </td>
                          <td>{order.items.length}</td>
                          <td>{formatCurrency(order.totalAmount)}</td>
                          <td><span className={`status-badge status-${order.status}`}>{order.status}</span></td>
                          <td>{order.paymentMethod || 'cash'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {/* Mobile Cards */}
                  <div className="mobile-data-cards">
                    {(reportData?.orders || []).map(order => (
                      <div key={order._id} className="mobile-card">
                        <div className="mobile-card-row">
                          <span className="mobile-card-label">Order ID</span>
                          <span className="mobile-card-value">{order._id.slice(-8).toUpperCase()}</span>
                        </div>
                        <div className="mobile-card-row">
                          <span className="mobile-card-label">Date</span>
                          <span className="mobile-card-value">{formatDate(order.createdAt)}</span>
                        </div>
                        <div className="mobile-card-row">
                          <span className="mobile-card-label">Customer</span>
                          <span className="mobile-card-value">
                            {(order.deliveryDetails && (order.deliveryDetails.firstName || order.deliveryDetails.lastName))
                              ? `${order.deliveryDetails.firstName || ''} ${order.deliveryDetails.lastName || ''}`.trim()
                              : (order.user ? `${order.user.firstName || ''} ${order.user.lastName || ''}`.trim() : 'N/A')}
                          </span>
                        </div>
                        <div className="mobile-card-row">
                          <span className="mobile-card-label">Items</span>
                          <span className="mobile-card-value">{order.items.length}</span>
                        </div>
                        <div className="mobile-card-row">
                          <span className="mobile-card-label">Total</span>
                          <span className="mobile-card-value">{formatCurrency(order.totalAmount)}</span>
                        </div>
                        <div className="mobile-card-row">
                          <span className="mobile-card-label">Status</span>
                          <span className="mobile-card-value">
                            <span className={`status-badge status-${order.status}`}>{order.status}</span>
                          </span>
                        </div>
                        <div className="mobile-card-row">
                          <span className="mobile-card-label">Payment</span>
                          <span className="mobile-card-value">{order.paymentMethod || 'cash'}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}

            {/* INVENTORY LIST */}
            {reportType === 'inventory-list' && (
              <>
                <div className="report-summary">
                  <div className="summary-card">
                    <h4>Total Products</h4>
                    <p className="summary-value">{reportData.summary.totalProducts}</p>
                  </div>
                  <div className="summary-card">
                    <h4>Total Inventory Value</h4>
                    <p className="summary-value">{formatCurrency(reportData.summary.totalValue)}</p>
                  </div>
                  <div className="summary-card">
                    <h4>In Stock</h4>
                    <p className="summary-value">{reportData.summary.inStock}</p>
                  </div>
                  <div className="summary-card">
                    <h4>Low/Out of Stock</h4>
                    <p className="summary-value">{reportData.summary.lowStock + reportData.summary.outOfStock}</p>
                  </div>
                </div>

                <div className="report-section">
                  <h4>Product Inventory</h4>
                  <table className="report-table">
                    <thead>
                      <tr>
                        <th>Product Name</th>
                        <th>Category</th>
                        <th>Stock Qty</th>
                        <th>Unit Price</th>
                        <th>Total Value</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(reportData?.products || []).map((product = {}) => {
                        const price = typeof product.price === 'number' ? product.price : parseFloat(product.price) || 0;
                        const discountType = product.discountType || 'none';
                        const discount = typeof product.discount === 'number' ? product.discount : parseFloat(product.discount) || 0;
                        const finalPrice = discountType === 'fixed'
                          ? price - discount
                          : price * (1 - (discount || 0) / 100);
                        const totalValue = finalPrice * (product.stock || 0);
                        const status = (product.stock || 0) > 10 ? 'In Stock' : (product.stock || 0) > 0 ? 'Low Stock' : 'Out of Stock';

                        return (
                          <tr key={product._id || product.name || Math.random()}>
                            <td>{product.name || 'N/A'}</td>
                            <td>{product.category || 'Uncategorized'}</td>
                            <td>{product.stock ?? 0}</td>
                            <td>{formatCurrency(finalPrice)}</td>
                            <td>{formatCurrency(totalValue)}</td>
                            <td><span className={`stock-badge stock-${status.replace(' ', '-').toLowerCase()}`}>{status}</span></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  {/* Mobile Cards */}
                  <div className="mobile-data-cards">
                    {(reportData?.products || []).map((product = {}) => {
                      const price = typeof product.price === 'number' ? product.price : parseFloat(product.price) || 0;
                      const discountType = product.discountType || 'none';
                      const discount = typeof product.discount === 'number' ? product.discount : parseFloat(product.discount) || 0;
                      const finalPrice = discountType === 'fixed'
                        ? price - discount
                        : price * (1 - (discount || 0) / 100);
                      const totalValue = finalPrice * (product.stock || 0);
                      const status = (product.stock || 0) > 10 ? 'In Stock' : (product.stock || 0) > 0 ? 'Low Stock' : 'Out of Stock';

                      return (
                        <div key={product._id || product.name || Math.random()} className="mobile-card">
                          <div className="mobile-card-row">
                            <span className="mobile-card-label">Product Name</span>
                            <span className="mobile-card-value">{product.name || 'N/A'}</span>
                          </div>
                          <div className="mobile-card-row">
                            <span className="mobile-card-label">Category</span>
                            <span className="mobile-card-value">{product.category || 'Uncategorized'}</span>
                          </div>
                          <div className="mobile-card-row">
                            <span className="mobile-card-label">Stock Qty</span>
                            <span className="mobile-card-value">{product.stock ?? 0}</span>
                          </div>
                          <div className="mobile-card-row">
                            <span className="mobile-card-label">Unit Price</span>
                            <span className="mobile-card-value">{formatCurrency(finalPrice)}</span>
                          </div>
                          <div className="mobile-card-row">
                            <span className="mobile-card-label">Total Value</span>
                            <span className="mobile-card-value">{formatCurrency(totalValue)}</span>
                          </div>
                          <div className="mobile-card-row">
                            <span className="mobile-card-label">Status</span>
                            <span className="mobile-card-value">
                              <span className={`stock-badge stock-${status.replace(' ', '-').toLowerCase()}`}>{status}</span>
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </>
            )}

            {/* PRODUCT MASTER LIST */}
            {reportType === 'product-master-list' && (
              <>
                <div className="report-summary">
                  <div className="summary-card">
                    <h4>Total Products</h4>
                    <p className="summary-value">{reportData.summary.totalProducts}</p>
                  </div>
                  <div className="summary-card">
                    <h4>Categories</h4>
                    <p className="summary-value">{reportData.summary.totalCategories}</p>
                  </div>
                </div>

                <div className="report-section">
                  <h4>Complete Product List</h4>
                  <table className="report-table">
                    <thead>
                      <tr>
                        <th>Product Code</th>
                        <th>Name</th>
                        <th>Description</th>
                        <th>Category</th>
                        <th>Price</th>
                        <th>Discount</th>
                        <th>Stock</th>
                        <th>Featured</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(reportData?.products || []).map((product = {}) => (
                        <tr key={product._id || product.name || Math.random()}>
                          <td>{String(product._id || '').slice(-6).toUpperCase()}</td>
                          <td>{product.name || 'N/A'}</td>
                          <td>{product.description ? `${product.description.substring(0, 50)}...` : ''}</td>
                          <td>{product.category || 'Uncategorized'}</td>
                          <td>{formatCurrency(product.price)}</td>
                          <td>{product.discountType === 'fixed' ? formatCurrency(product.discount) : `${product.discount || 0}%`}</td>
                          <td>{product.stock ?? 0}</td>
                          <td>{product.featured ? '⭐' : '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {/* Mobile Cards */}
                  <div className="mobile-data-cards">
                    {(reportData?.products || []).map((product = {}) => (
                      <div key={product._id || product.name || Math.random()} className="mobile-card">
                        <div className="mobile-card-row">
                          <span className="mobile-card-label">Product Code</span>
                          <span className="mobile-card-value">{String(product._id || '').slice(-6).toUpperCase()}</span>
                        </div>
                        <div className="mobile-card-row">
                          <span className="mobile-card-label">Name</span>
                          <span className="mobile-card-value">{product.name || 'N/A'}</span>
                        </div>
                        <div className="mobile-card-row">
                          <span className="mobile-card-label">Description</span>
                          <span className="mobile-card-value">{product.description ? `${product.description.substring(0, 50)}...` : ''}</span>
                        </div>
                        <div className="mobile-card-row">
                          <span className="mobile-card-label">Category</span>
                          <span className="mobile-card-value">{product.category || 'Uncategorized'}</span>
                        </div>
                        <div className="mobile-card-row">
                          <span className="mobile-card-label">Price</span>
                          <span className="mobile-card-value">{formatCurrency(product.price)}</span>
                        </div>
                        <div className="mobile-card-row">
                          <span className="mobile-card-label">Discount</span>
                          <span className="mobile-card-value">{product.discountType === 'fixed' ? formatCurrency(product.discount) : `${product.discount || 0}%`}</span>
                        </div>
                        <div className="mobile-card-row">
                          <span className="mobile-card-label">Stock</span>
                          <span className="mobile-card-value">{product.stock ?? 0}</span>
                        </div>
                        <div className="mobile-card-row">
                          <span className="mobile-card-label">Featured</span>
                          <span className="mobile-card-value">{product.featured ? '⭐' : '-'}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}

            {/* FAST MOVING PRODUCTS */}
            {reportType === 'fast-moving' && (
              <>
                <div className="report-summary">
                  <div className="summary-card">
                    <h4>Total Products</h4>
                    <p className="summary-value">{reportData.summary.totalProducts}</p>
                  </div>
                  <div className="summary-card">
                    <h4>Total Quantity Sold</h4>
                    <p className="summary-value">{reportData.summary.totalQuantitySold}</p>
                  </div>
                  <div className="summary-card">
                    <h4>Total Revenue</h4>
                    <p className="summary-value">{formatCurrency(reportData.summary.totalRevenue)}</p>
                  </div>
                </div>

                <div className="report-section">
                  <h4>Top Selling Products</h4>
                  <table className="report-table">
                    <thead>
                      <tr>
                        <th>Rank</th>
                        <th>Product Name</th>
                        <th>Category</th>
                        <th>Quantity Sold</th>
                        <th>Revenue</th>
                        <th>Orders</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(reportData?.products || []).map((item = {}, index) => (
                        <tr key={item._id || index}>
                          <td>{index + 1}</td>
                          <td>{item.product?.name || 'N/A'}</td>
                          <td>{item.product?.category || 'Uncategorized'}</td>
                          <td>{item.totalQuantity ?? 0}</td>
                          <td>{formatCurrency(item.totalRevenue)}</td>
                          <td>{item.orderCount ?? 0}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {/* Mobile Cards */}
                  <div className="mobile-data-cards">
                    {(reportData?.products || []).map((item = {}, index) => (
                      <div key={item._id || index} className="mobile-card">
                        <div className="mobile-card-row">
                          <span className="mobile-card-label">Rank</span>
                          <span className="mobile-card-value">{index + 1}</span>
                        </div>
                        <div className="mobile-card-row">
                          <span className="mobile-card-label">Product Name</span>
                          <span className="mobile-card-value">{item.product?.name || 'N/A'}</span>
                        </div>
                        <div className="mobile-card-row">
                          <span className="mobile-card-label">Category</span>
                          <span className="mobile-card-value">{item.product?.category || 'Uncategorized'}</span>
                        </div>
                        <div className="mobile-card-row">
                          <span className="mobile-card-label">Quantity Sold</span>
                          <span className="mobile-card-value">{item.totalQuantity ?? 0}</span>
                        </div>
                        <div className="mobile-card-row">
                          <span className="mobile-card-label">Revenue</span>
                          <span className="mobile-card-value">{formatCurrency(item.totalRevenue)}</span>
                        </div>
                        <div className="mobile-card-row">
                          <span className="mobile-card-label">Orders</span>
                          <span className="mobile-card-value">{item.orderCount ?? 0}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}

            {/* SLOW MOVING PRODUCTS */}
            {reportType === 'slow-moving' && (
              <>
                <div className="report-summary">
                  <div className="summary-card">
                    <h4>Total Products</h4>
                    <p className="summary-value">{reportData.summary.totalProducts}</p>
                  </div>
                  <div className="summary-card">
                    <h4>Total Stock Value</h4>
                    <p className="summary-value">{formatCurrency(reportData.summary.totalStockValue)}</p>
                  </div>
                </div>

                <div className="report-section">
                  <h4>Slow Moving Inventory</h4>
                  <table className="report-table">
                    <thead>
                      <tr>
                        <th>Product Name</th>
                        <th>Category</th>
                        <th>Current Stock</th>
                        <th>Quantity Sold</th>
                        <th>Sales Velocity</th>
                        <th>Days Since Added</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(reportData?.products || []).map((item = {}) => (
                        <tr key={item.product?._id || Math.random()}>
                          <td>{item.product?.name || 'N/A'}</td>
                          <td>{item.product?.category || 'Uncategorized'}</td>
                          <td>{item.stock ?? 0}</td>
                          <td>{item.totalQuantity ?? 0}</td>
                          <td>{(item.salesVelocity ?? 0).toFixed(4)}</td>
                          <td>{item.daysSinceCreated ?? 0}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {/* Mobile Cards */}
                  <div className="mobile-data-cards">
                    {(reportData?.products || []).map((item = {}) => (
                      <div key={item.product?._id || Math.random()} className="mobile-card">
                        <div className="mobile-card-row">
                          <span className="mobile-card-label">Product Name</span>
                          <span className="mobile-card-value">{item.product?.name || 'N/A'}</span>
                        </div>
                        <div className="mobile-card-row">
                          <span className="mobile-card-label">Category</span>
                          <span className="mobile-card-value">{item.product?.category || 'Uncategorized'}</span>
                        </div>
                        <div className="mobile-card-row">
                          <span className="mobile-card-label">Current Stock</span>
                          <span className="mobile-card-value">{item.stock ?? 0}</span>
                        </div>
                        <div className="mobile-card-row">
                          <span className="mobile-card-label">Quantity Sold</span>
                          <span className="mobile-card-value">{item.totalQuantity ?? 0}</span>
                        </div>
                        <div className="mobile-card-row">
                          <span className="mobile-card-label">Sales Velocity</span>
                          <span className="mobile-card-value">{(item.salesVelocity ?? 0).toFixed(4)}</span>
                        </div>
                        <div className="mobile-card-row">
                          <span className="mobile-card-label">Days Since Added</span>
                          <span className="mobile-card-value">{item.daysSinceCreated ?? 0}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}

            {/* INVENTORY CHANGES */}
            {reportType === 'inventory-changes' && (
              <>
                <div className="report-summary">
                  <div className="summary-card">
                    <h4>Total Products</h4>
                    <p className="summary-value">{reportData.summary.totalProducts}</p>
                  </div>
                  <div className="summary-card">
                    <h4>Total Sold</h4>
                    <p className="summary-value">{reportData.summary.totalSold}</p>
                  </div>
                </div>

                <div className="report-section">
                  <h4>Inventory Movement</h4>
                  <table className="report-table">
                    <thead>
                      <tr>
                        <th>Item Code</th>
                        <th>Item Name</th>
                        <th>Category</th>
                        <th>Opening Stock</th>
                        <th>Received</th>
                        <th>Sold</th>
                        <th>Closing Stock</th>
                        <th>Variance</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(reportData?.changes || []).map(change => (
                        <tr key={change.itemCode}>
                          <td>{change.itemCode}</td>
                          <td>{change.itemName}</td>
                          <td>{change.category}</td>
                          <td>{change.openingStock}</td>
                          <td>{change.receivedStock}</td>
                          <td>{change.soldStock}</td>
                          <td>{change.closingStock}</td>
                          <td className={change.variance < 0 ? 'negative' : change.variance > 0 ? 'positive' : ''}>{change.variance}</td>
                          <td><span className={`status-badge status-${change.status.toLowerCase()}`}>{change.status}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {/* Mobile Cards */}
                  <div className="mobile-data-cards">
                    {(reportData?.changes || []).map(change => (
                      <div key={change.itemCode} className="mobile-card">
                        <div className="mobile-card-row">
                          <span className="mobile-card-label">Item Code</span>
                          <span className="mobile-card-value">{change.itemCode}</span>
                        </div>
                        <div className="mobile-card-row">
                          <span className="mobile-card-label">Item Name</span>
                          <span className="mobile-card-value">{change.itemName}</span>
                        </div>
                        <div className="mobile-card-row">
                          <span className="mobile-card-label">Category</span>
                          <span className="mobile-card-value">{change.category}</span>
                        </div>
                        <div className="mobile-card-row">
                          <span className="mobile-card-label">Opening Stock</span>
                          <span className="mobile-card-value">{change.openingStock}</span>
                        </div>
                        <div className="mobile-card-row">
                          <span className="mobile-card-label">Received</span>
                          <span className="mobile-card-value">{change.receivedStock}</span>
                        </div>
                        <div className="mobile-card-row">
                          <span className="mobile-card-label">Sold</span>
                          <span className="mobile-card-value">{change.soldStock}</span>
                        </div>
                        <div className="mobile-card-row">
                          <span className="mobile-card-label">Closing Stock</span>
                          <span className="mobile-card-value">{change.closingStock}</span>
                        </div>
                        <div className="mobile-card-row">
                          <span className="mobile-card-label">Variance</span>
                          <span className={`mobile-card-value ${change.variance < 0 ? 'negative' : change.variance > 0 ? 'positive' : ''}`}>{change.variance}</span>
                        </div>
                        <div className="mobile-card-row">
                          <span className="mobile-card-label">Status</span>
                          <span className="mobile-card-value">
                            <span className={`status-badge status-${change.status.toLowerCase()}`}>{change.status}</span>
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}

            <div className="report-footer">
              <p>This is a computer-generated report from Chiqui's Garden Business Management System</p>
              <p>For inquiries, contact: chiquisgardenlandscapingservic@gmail.com</p>
            </div>
          </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
    </div>
  );
};

export default AdminReports;
